import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "grid grid-cols-2 gap-6"
}
const _hoisted_2 = {
  key: 0,
  class: "border border-dotted border-gray-400 rounded p-6",
  "data-dusk": "power-selection-negative-powers"
}
const _hoisted_3 = { class: "flex justify-between mb-2" }
const _hoisted_4 = { class: "text-sm font-semibold" }
const _hoisted_5 = { class: "text-sm font-semibold pr-4" }
const _hoisted_6 = {
  key: 1,
  class: "border border-dotted border-gray-400 rounded p-6",
  "data-dusk": "power-selection-positive-powers"
}
const _hoisted_7 = { class: "flex justify-between mb-2" }
const _hoisted_8 = { class: "text-sm font-semibold" }
const _hoisted_9 = { class: "text-sm font-semibold pr-4" }

import { onMounted, ref, watch, computed } from 'vue';
import { NumberInput } from '@/lib/components/Input';
import { useI18n } from 'vue-i18n';
import { PowerQuantity } from '@/models/webshop.model';
import { WEBSHOP_MIN_STOCK_ORDER_QUANTITY, WEBSHOP_MAX_STOCK_ORDER_QUANTITY } from '@/constants';
import { useQuantityValidation } from '@/composables/use-quantity-validation';


export default /*@__PURE__*/_defineComponent({
  __name: 'PowerSelection',
  props: {
    modelValue: { type: Boolean },
    powerOptions: {},
    readonly: { type: Boolean }
  },
  emits: ["update:modelValue", "update:powerOnBlur", "update:powerQuantities"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();
const { validateQuantityInput, handleQuantityChange, handleQuantityBlur } = useQuantityValidation(ref(null), { minValue: 0 });

const powerQuantities = ref<{ power: string; quantity: number | null }[]>([]);

let previousPowerQuantities = '';

function updatePreviousState() {
  previousPowerQuantities = JSON.stringify(
    powerQuantities.value.map((item) => ({
      power: item.power,
      quantity: validateQuantity(item.quantity)
    }))
  );
}

onMounted(() => {
  powerQuantities.value = props.powerOptions.map((opt) => ({
    power: opt.label,
    quantity: opt.quantity ? validateQuantity(opt.quantity) : null
  }));
  updatePreviousState();
});

const negativePowers = computed(() => {
  const negatives = powerQuantities.value.filter((p) => parseFloat(p.power) < 0);
  return negatives.sort((a, b) => parseFloat(b.power) - parseFloat(a.power));
});

const positivePowers = computed(() => {
  const positives = powerQuantities.value.filter((p) => parseFloat(p.power) >= 0);
  return positives.sort((a, b) => parseFloat(a.power) - parseFloat(b.power));
});

const validateQuantity = (quantity: number | null): number | null => {
  if (quantity === null) {
    return null;
  }
  return Math.min(Math.max(quantity, WEBSHOP_MIN_STOCK_ORDER_QUANTITY), WEBSHOP_MAX_STOCK_ORDER_QUANTITY);
};

watch(
  () => JSON.stringify(powerQuantities.value.map((item) => ({
    power: item.power,
    quantity: validateQuantity(item.quantity)
  }))),
  (newValStr) => {
    if (newValStr !== previousPowerQuantities) {
      previousPowerQuantities = newValStr;
      const validatedQuantities = JSON.parse(newValStr);
      emit('update:powerQuantities', validatedQuantities.filter((q: { quantity: number | null }) => q.quantity !== null));
    }
  }
);

function reset() {
  powerQuantities.value = props.powerOptions.map((opt) => ({
    power: opt.label,
    quantity: null
  }));
  emit('update:powerQuantities', powerQuantities.value);
}

const handleQuantityInput = (event: Event, opt: { power: string; quantity: number | null }) => {
  const input = event.target as HTMLInputElement;
  const cleanedValue = input.value.replace(/[^0-9]/g, '');

  if (cleanedValue === '') {
    opt.quantity = null;
    input.value = '';
    return;
  }

  const parsedValue = parseInt(cleanedValue, 10);
  const validatedValue = validateQuantity(parsedValue);
  opt.quantity = validatedValue;
  input.value = validatedValue.toString();
};

const onQuantityBlur = (event: Event, opt: { power: string; quantity: number | null }) => {
  emit('update:powerOnBlur', opt);
  handleQuantityBlur(event);
};
__expose({ reset });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_ctx.modelValue)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (negativePowers.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("h3", _hoisted_4, _toDisplayString(_unref(t)('platform.webshop.power')), 1),
                  _createElementVNode("h3", _hoisted_5, _toDisplayString(_unref(t)('platform.webshop.quantity')), 1)
                ]),
                _createElementVNode("div", {
                  class: "overflow-y-auto pr-2",
                  style: _normalizeStyle({ maxHeight: negativePowers.value.length > 10 ? '30rem' : 'none' })
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(negativePowers.value, (opt) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: opt.power,
                      class: "flex justify-between items-center py-1"
                    }, [
                      _createElementVNode("span", null, _toDisplayString(parseFloat(opt.power).toFixed(2)), 1),
                      _createVNode(_unref(NumberInput), {
                        modelValue: opt.quantity,
                        "onUpdate:modelValue": ($event: any) => ((opt.quantity) = $event),
                        class: "w-20 text-sm",
                        size: "xs",
                        readonly: props.readonly,
                        min: _unref(WEBSHOP_MIN_STOCK_ORDER_QUANTITY),
                        max: _unref(WEBSHOP_MAX_STOCK_ORDER_QUANTITY),
                        onKeydown: _unref(validateQuantityInput),
                        onInput: (event) => { handleQuantityInput(event, opt); _unref(handleQuantityChange)(event); },
                        onBlurChanges: (event) => onQuantityBlur(event, opt)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "readonly", "min", "max", "onKeydown", "onInput", "onBlurChanges"])
                    ]))
                  }), 128))
                ], 4)
              ]))
            : _createCommentVNode("", true),
          (positivePowers.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("h3", _hoisted_8, _toDisplayString(_unref(t)('platform.webshop.power')), 1),
                  _createElementVNode("h3", _hoisted_9, _toDisplayString(_unref(t)('platform.webshop.quantity')), 1)
                ]),
                _createElementVNode("div", {
                  class: "overflow-y-auto pr-2",
                  style: _normalizeStyle({ maxHeight: positivePowers.value.length > 10 ? '30rem' : 'none' })
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(positivePowers.value, (opt) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: opt.power,
                      class: "flex justify-between items-center py-1"
                    }, [
                      _createElementVNode("span", null, "+" + _toDisplayString(parseFloat(opt.power).toFixed(2)), 1),
                      _createVNode(_unref(NumberInput), {
                        modelValue: opt.quantity,
                        "onUpdate:modelValue": ($event: any) => ((opt.quantity) = $event),
                        class: "w-20 text-sm",
                        size: "xs",
                        readonly: props.readonly,
                        min: _unref(WEBSHOP_MIN_STOCK_ORDER_QUANTITY),
                        max: _unref(WEBSHOP_MAX_STOCK_ORDER_QUANTITY),
                        onKeydown: _unref(validateQuantityInput),
                        onInput: (event) => { handleQuantityInput(event, opt); _unref(handleQuantityChange)(event); },
                        onBlurChanges: (event) => onQuantityBlur(event, opt)
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "readonly", "min", "max", "onKeydown", "onInput", "onBlurChanges"])
                    ]))
                  }), 128))
                ], 4)
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})